import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../hooks/useAuth';
import { BarChart3, TrendingUp, Activity, Clock } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.frolic.so';

interface Project {
  id: string;
  name: string;
  apiKey: string;
}

function AnalyticsPage() {
  const { accessToken } = useAuth();
  const [projectId, setProjectId] = useState('');
  const [timeRange, setTimeRange] = useState('24h');

  const { data: projectsData } = useQuery<{ projects: Project[] }>({
    queryKey: ['projects'],
    queryFn: async () => {
      if (!accessToken) throw new Error('Not authenticated');
      const response = await fetch(`${API_URL}/v1/user/projects`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) throw new Error('Failed to fetch projects');
      return response.json();
    },
    enabled: !!accessToken,
  });

  const projects = projectsData?.projects || [];

  useEffect(() => {
    if (projects.length > 0 && !projectId) {
      setProjectId(projects[0].id);
    }
  }, [projects, projectId]);

  const stats = [
    {
      label: 'Total Events',
      value: '—',
      change: '',
      icon: Activity,
      trend: 'neutral',
    },
    {
      label: 'Error Rate',
      value: '—',
      change: '',
      icon: TrendingUp,
      trend: 'neutral',
    },
    {
      label: 'Avg Latency',
      value: '—',
      change: '',
      icon: Clock,
      trend: 'neutral',
    },
    {
      label: 'Storage Used',
      value: '—',
      change: '',
      icon: BarChart3,
      trend: 'neutral',
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Analytics</h1>
          <p className="text-muted-foreground">Monitor your application performance</p>
        </div>
        <div className="flex gap-3">
          <Select value={projectId} onValueChange={setProjectId}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Select project" />
            </SelectTrigger>
            <SelectContent>
              {projects.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1h">Last 1 hour</SelectItem>
              <SelectItem value="24h">Last 24 hours</SelectItem>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => (
          <Card key={stat.label}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {stat.label}
              </CardTitle>
              <stat.icon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-foreground">{stat.value}</div>
              {stat.change && (
                <p
                  className={`text-xs ${stat.trend === 'up' ? 'text-green-500' : stat.trend === 'down' ? 'text-red-500' : 'text-muted-foreground'}`}
                >
                  {stat.change} from last period
                </p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Log Volume</CardTitle>
            <CardDescription>Events ingested over time</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex h-[200px] items-center justify-center rounded-lg bg-muted">
              <p className="text-sm text-muted-foreground">Chart coming soon</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Error Distribution</CardTitle>
            <CardDescription>Errors by service</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex h-[200px] items-center justify-center rounded-lg bg-muted">
              <p className="text-sm text-muted-foreground">Chart coming soon</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Top Error Messages</CardTitle>
          <CardDescription>Most frequent errors in the selected period</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex h-[120px] items-center justify-center text-muted-foreground">
            <p className="text-sm">No error data available. Start ingesting logs to see analytics.</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export const Route = createFileRoute('/analytics')({
  component: AnalyticsPage,
});
